import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Container, Paper, Chip } from '@mui/material';
import { useLanguage } from '../i18n/LanguageContext';
import { motion } from 'framer-motion';
import heroBg from "../imaged/growth-close-up-environmental-lush-natural_1172-349.jpg";
import leafImg from "../imaged/ab.jpg";
import shalyaImg from "../imaged/Shalya Tantra.jpg";
import kayaImg from "../imaged/Kayachikitsa.jpg";
import SpaIcon from '@mui/icons-material/Spa';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import ScienceIcon from '@mui/icons-material/Science';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import BalanceIcon from '@mui/icons-material/Balance';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AirIcon from '@mui/icons-material/Air';
import LandscapeIcon from '@mui/icons-material/Landscape';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';

const MotionBox = motion(Box);
const MotionCard = motion(Card);

const AboutAyurveda = () => {
  const { t } = useLanguage();
  const branches = t('aboutAyurveda.branches');
  const doshas = t('aboutAyurveda.doshas');
  const elements = t('aboutAyurveda.elements');
  const texts = t('aboutAyurveda.texts');

  const branchList = Array.isArray(branches) ? branches : [];
  const doshaList = Array.isArray(doshas) ? doshas : [];
  const elementList = Array.isArray(elements) ? elements : [];
  const textList = Array.isArray(texts) ? texts : [];

  const elementIcons = [
    <AllInclusiveIcon />, // Space
    <AirIcon />,          // Air
    <LocalFireDepartmentIcon />, // Fire
    <WaterDropIcon />,    // Water
    <LandscapeIcon />     // Earth
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };

  return (
    <Box sx={{ overflowX: 'hidden' }}>
      {/* Hero Section with Parallax-like effect */}
      <Box 
        sx={{ 
          position: 'relative', 
          height: '85vh', 
          minHeight: 600,
          backgroundImage: `url("${heroBg}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7))'
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <MotionBox 
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="overline" sx={{ color: '#81c784', letterSpacing: 4, fontSize: '1.2rem', fontWeight: 700, display: 'block', mb: 2 }}>
              {t('aboutAyurveda.overline')}
            </Typography>
            <Typography variant="h1" sx={{ color: '#fff', fontWeight: 800, mb: 3, fontSize: { xs: '3rem', md: '5.5rem' }, lineHeight: 1.1 }}>
              {t('aboutAyurveda.title')}
            </Typography>
            <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.95)', maxWidth: 800, mx: 'auto', lineHeight: 1.6, fontWeight: 300, fontSize: '1.4rem' }}>
              {t('aboutAyurveda.subtitle')}
            </Typography>
          </MotionBox>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 8 }}>
        <MotionBox 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          
          {/* Introduction & History - Split View */}
          <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
            <Grid item xs={12} md={6}>
              <MotionBox variants={itemVariants}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <SpaIcon sx={{ color: 'primary.main', fontSize: 32, mr: 2 }} />
                  <Typography variant="h4" fontWeight={700} color="text.primary">
                    The Science of Life
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary', mb: 3 }}>
                  {t('aboutAyurveda.intro')}
                </Typography>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 3, 
                    bgcolor: 'primary.50', 
                    borderLeft: '4px solid', 
                    borderColor: 'primary.main',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.primary', fontWeight: 500 }}>
                    "{t('aboutAyurveda.history')}"
                  </Typography>
                </Paper>
              </MotionBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MotionBox variants={itemVariants}>
                <Box 
                  component="img"
                  src={leafImg}
                  alt="Ayurveda History"
                  sx={{ 
                    width: '100%', 
                    borderRadius: 8,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    transform: 'rotate(2deg)',
                    transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'rotate(0deg)' }
                  }}
                />
              </MotionBox>
            </Grid>
          </Grid>

          {/* Dhanvantari Section */}
          <Box sx={{ mb: 10 }}>
            <MotionBox variants={itemVariants}>
               <Card sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' },
                  overflow: 'hidden', 
                  borderRadius: 6,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                  bgcolor: '#fff3e0'
                }}>
                  <Box sx={{ 
                    width: { xs: '100%', md: 200 }, 
                    bgcolor: '#ffb74d', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    p: 4
                  }}>
                    <MedicalServicesIcon sx={{ fontSize: 80, color: 'white' }} />
                  </Box>
                  <CardContent sx={{ p: { xs: 4, md: 6 }, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: '#e65100' }}>
                      {t('aboutParitranam.dhanvantariTitle')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary', whiteSpace: 'pre-line' }}>
                      {t('aboutParitranam.dhanvantariText')}
                    </Typography>
                  </CardContent>
               </Card>
            </MotionBox>
          </Box>

          {/* Pancha Mahabhuta Section */}
          <Box sx={{ mb: 10, textAlign: 'center' }}>
            <MotionBox variants={itemVariants}>
              <Typography variant="h3" fontWeight={800} sx={{ mb: 1, color: 'primary.dark' }}>
                {t('aboutAyurveda.elementsTitle')}
              </Typography>
              <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 6 }}>
                The Five Great Elements that compose the universe and the human body
              </Typography>
              
              <Grid container spacing={3} justifyContent="center">
                {elementList.map((el, i) => (
                  <Grid key={i} item xs={6} sm={4} md={2}>
                    <MotionCard 
                      whileHover={{ y: -10 }}
                      sx={{ 
                        height: '100%', 
                        borderRadius: 4, 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 3,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                        bgcolor: '#fff'
                      }}
                    >
                      <Box sx={{ 
                        width: 60, height: 60, 
                        borderRadius: '50%', 
                        bgcolor: `primary.${i % 2 === 0 ? 'light' : 'main'}`, 
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', mb: 2
                      }}>
                        {elementIcons[i] || <SpaIcon />}
                      </Box>
                      <Typography variant="h6" fontWeight={700} color="text.primary">
                        {el}
                      </Typography>
                    </MotionCard>
                  </Grid>
                ))}
              </Grid>
            </MotionBox>
          </Box>

          {/* Tridosha Theory - Full Width Feature */}
          <Paper 
            sx={{ 
              mb: 10, 
              borderRadius: 6, 
              overflow: 'hidden', 
              boxShadow: '0 10px 40px rgba(46, 125, 50, 0.1)',
              background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
              color: '#fff',
              position: 'relative'
            }}
          >
            <Grid container>
              <Grid item xs={12} md={7} sx={{ p: { xs: 4, md: 6 }, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <BalanceIcon sx={{ fontSize: 40, mr: 2, opacity: 0.9 }} />
                  <Typography variant="h3" fontWeight={800}>
                    {t('aboutAyurveda.tridoshaTitle')}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, opacity: 0.9, mb: 4, whiteSpace: 'pre-line' }}>
                  {t('aboutAyurveda.tridoshaText')}
                </Typography>
                
                <Grid container spacing={2}>
                  {doshaList.map((d, i) => (
                    <Grid key={i} item xs={12} sm={4}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'rgba(255,255,255,0.1)', 
                        backdropFilter: 'blur(10px)',
                        borderRadius: 3,
                        textAlign: 'center',
                        border: '1px solid rgba(255,255,255,0.2)'
                      }}>
                        <Typography variant="h6" fontWeight={700}>{d}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              <Grid item xs={12} md={5} sx={{ minHeight: 300 }}>
                <Box 
                  sx={{ 
                    height: '100%', 
                    width: '100%',
                    backgroundImage: `url("${kayaImg}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: 0.8,
                    mixBlendMode: 'overlay'
                  }} 
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Classical Texts & Branches */}
          <Grid container spacing={6}>
            <Grid item xs={12} md={5}>
              <MotionBox variants={itemVariants}>
                <Card sx={{ borderRadius: 4, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                  <Box sx={{ height: 200, overflow: 'hidden' }}>
                    <Box component="img" src={shalyaImg} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <AutoStoriesIcon sx={{ color: 'primary.main', fontSize: 28, mr: 2 }} />
                      <Typography variant="h4" fontWeight={700}>
                        {t('aboutAyurveda.textsTitle')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {textList.map((txt, i) => (
                        <Chip 
                          key={i} 
                          label={txt} 
                          sx={{ 
                            fontSize: '0.95rem', 
                            py: 2.5, px: 1, 
                            borderRadius: 2,
                            bgcolor: 'primary.50',
                            color: 'primary.dark',
                            fontWeight: 600
                          }} 
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </MotionBox>
            </Grid>

            <Grid item xs={12} md={7}>
              <MotionBox variants={itemVariants}>
                <Typography variant="h4" fontWeight={700} sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
                  <ScienceIcon sx={{ mr: 2, color: 'primary.main' }} />
                  {t('aboutAyurveda.ashtangaTitle')}
                </Typography>
                <Grid container spacing={2}>
                  {branchList.map((b, i) => (
                    <Grid key={i} item xs={12} sm={6}>
                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 2.5, 
                          borderRadius: 3, 
                          border: '1px solid', 
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'all 0.3s ease',
                          '&:hover': { 
                            borderColor: 'primary.main', 
                            bgcolor: 'primary.50',
                            transform: 'translateX(5px)'
                          }
                        }}
                      >
                        <Box sx={{ 
                          width: 40, height: 40, 
                          borderRadius: 2, 
                          bgcolor: 'primary.main', 
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'white', mr: 2,
                          fontSize: '0.9rem', fontWeight: 700
                        }}>
                          {i + 1}
                        </Box>
                        <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                          {b}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </MotionBox>
            </Grid>
          </Grid>

        </MotionBox>
      </Container>
    </Box>
  );
};

export default AboutAyurveda;